import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
    Card,
    Tabs,
    Tag,
    Avatar,
    Input,
    Button,
    Select,
    DatePicker,
    Modal,
    Spin,
    message,
    Dropdown,
} from "antd";
import { PhoneOutlined, EditOutlined, SaveOutlined, FacebookFilled, GoogleOutlined, LeftOutlined, RightOutlined, CheckOutlined, SyncOutlined, LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { filterOptionVietnamese } from "../../lib/utils";
import Layout from '../../Layout'
import NoteBox from "components/NoteBox";
import HistoryList from "components/HistoryList";
import HairClinicForm from "components/ChuyenMon/HairClinicForm";
import { useTranslation } from "react-i18next";
import viVN from 'antd/es/date-picker/locale/vi_VN';
import enUS from 'antd/es/date-picker/locale/en_US';
import MikoReceptionPage from "components/ChuyenMon/MikoReceptionPage";
import CosmeticSurgeryReception from "components/ChuyenMon/CosmeticSurgeryReception";
import ThreadLiftReception from "components/ChuyenMon/ThreadLiftReception";
import ConsultationForm from "components/ChuyenMon/ConsultationForm";
import ConsultationTabs from "components/ChuyenMon/ConsultationTabs";
import BeautyConsultation from "components/ChuyenMon/BeautyConsultation";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getAppointmentDetailPath } from "../../utils/appointmentRoute";
import { getAppointmentConsultationDetail, AppointmentItem } from "../../api/Appointment";
import { updateChannelCustomer } from "../../api/ChannelCustomer";
import useAuthStore from "../../store/authStore";
import { mixGetV1, mixPutV1 } from "../../services/mixin";
import { useDebounce } from "../../hooks/useDebounce";
import { useLocationData } from "../../hooks/useLocationData";
import { getWardsByProvince } from "../../api/LocationInfo";
import { MessageSquare, Phone, Users, CircleDashed } from "lucide-react";
import CustomerService from "components/CustomerService";
import AppointmentPosOrder from "components/AppointmentPosOrder";
import CustomerImageGallery from "components/CustomerImageGallery";
import AppointmentScheduleBox from "components/AppointmentScheduleBox";
import { getNextTags } from '../../utils/appointmentNextTags';

type CustomerFormState = {
    name: string;
    phone: string;
    email: string;
    pancake: string;
    pageStaffId: number | null;
    telesaleId: number | null;
    branchStaffId: number | null;
    actionStaffId: number | null;
    source: string;
    gender: string;
    birthday: string;
    leadSource: string;
    serviceIds: number[];
    status: number;
    customerTagId: number | null;
    address: string;
    country: string;
    city: string;
    ward: string;
};

type StaffOption = {
    value: number;
    label: string;
};

export default function CustomerDetailPage() {
    const { t, i18n } = useTranslation();
    const datePickerLocale = i18n.language === 'en' ? enUS : viVN;
    const dateDisplayFormat = i18n.language === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const CURRENT_PAGE = 2; // 1=lịch hẹn, 2=lịch tư vấn, 3=lịch thực hiện
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const hasEditPermission = hasPermission("consultation.edit");
    const hasChangeStatusPermission = hasPermission("consultation.change_status");
    // Nhân sự phụ trách lịch hẹn: chỉ super admin được đổi, quyền edit thường không đủ
    const isAdmin = isSuperAdmin;
    // Không có quyền riêng cho lịch sử: ai xem được trang thì xem được lịch sử chăm sóc
    const canViewHistory = isSuperAdmin || hasPermission("consultation.view");
    const [editing, setEditing] = useState(false);
    const [activeTab, setActiveTab] = useState("detail");
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [updatingTagField, setUpdatingTagField] = useState<string | null>(null);
    const [appointment, setAppointment] = useState<AppointmentItem | null>(null);

    // Ngoài quyền sửa, nhân viên còn phải được PHÂN CÔNG vào lịch hẹn này mới được chỉnh sửa;
    // không thuộc 4 vị trí phụ trách thì chỉ xem. Super admin không bị giới hạn này.
    const currentStaffId = useMemo(() => {
        try {
            const stored = JSON.parse(localStorage.getItem("user") || "{}");
            const sid = Number(stored?.info?.id);
            return Number.isFinite(sid) ? sid : null;
        } catch {
            return null;
        }
    }, []);

    const isAssigned = useMemo(() => {
        if (currentStaffId == null || !appointment) return false;
        return [
            (appointment as any).staff_id,
            (appointment as any).telesale_id,
            (appointment as any).branch_staff_id,
            (appointment as any).action_staff_id,
        ].some((v) => v != null && Number(v) === currentStaffId);
    }, [appointment, currentStaffId]);

    const canEdit = isSuperAdmin || (hasEditPermission && isAssigned);

    // Đổi trạng thái: quyền riêng `consultation.change_status`, vẫn phải được
    // phân công vào lịch. Quyền "Sửa" bao hàm đổi trạng thái để không thu hồi
    // quyền của các nhóm quyền đang chạy.
    const canChangeStatus =
        isSuperAdmin || ((hasChangeStatusPermission || hasEditPermission) && isAssigned);

    // Sale cơ sở phụ trách lịch này được phép chọn/đổi nhân viên Thực hiện
    const canEditActionStaff = isAdmin
        || (currentStaffId != null && Number((appointment as any)?.branch_staff_id) === currentStaffId);
    const [pageStaffOptions, setPageStaffOptions] = useState<StaffOption[]>([]);
    const [telesaleOptions, setTelesaleOptions] = useState<StaffOption[]>([]);
    const [branchStaffOptions, setBranchStaffOptions] = useState<StaffOption[]>([]);
    const [actionStaffOptions, setActionStaffOptions] = useState<StaffOption[]>([]);
    const [loadingStaffOptions, setLoadingStaffOptions] = useState(false);
    const [editingStaffField, setEditingStaffField] = useState<string | null>(null);
    const [editingStaffValue, setEditingStaffValue] = useState<number | null>(null);
    const [savingStaffField, setSavingStaffField] = useState(false);
    const [editingSimpleField, setEditingSimpleField] = useState<string | null>(null);
    const [editingSimpleValue, setEditingSimpleValue] = useState<any>(null);
    const [savingSimpleField, setSavingSimpleField] = useState(false);
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [addressModalForm, setAddressModalForm] = useState<{ city: string; ward: string; address: string }>({ city: '', ward: '', address: '' });
    const [addressModalWardOptions, setAddressModalWardOptions] = useState<{ label: string; value: string }[]>([]);
    const [addressModalLoadingWards, setAddressModalLoadingWards] = useState(false);
    const [savingAddress, setSavingAddress] = useState(false);

    const [editingServices, setEditingServices] = useState(false);
    const [editingServicesValue, setEditingServicesValue] = useState<number[]>([]);
    const [savingServices, setSavingServices] = useState(false);

    // Service search states
    const [serviceOptions, setServiceOptions] = useState<Array<{ value: number; label: string; treatmentSlipId?: number | null }>>([]);
    const [serviceSearchKw, setServiceSearchKw] = useState("");
    const debouncedServiceKw = useDebounce(serviceSearchKw, 400);
    const [serviceLoading, setServiceLoading] = useState(false);
    const [servicePage, setServicePage] = useState(1);
    const [serviceHasMore, setServiceHasMore] = useState(true);

    // Accumulates id->name so names are never lost when the dropdown list resets
    const serviceNameCache = useRef<Record<number, string>>({});
    const serviceSlipCache = useRef<Record<number, number>>({});

    const getServiceName = (sid: number) =>
        serviceNameCache.current[sid] || `#${sid}`;

    const resolveServiceLabel = (service: any): string => {
        return String(
            service?.name ||
            service?.title ||
            service?.service_name ||
            service?.service?.name ||
            `#${service?.id}`
        );
    };

    const dedupeServiceOptions = (
        options: Array<{ value: number; label: string; treatmentSlipId?: number | null }>
    ) => {
        const map = new Map<number, { value: number; label: string; treatmentSlipId?: number | null }>();
        options.forEach((opt) => {
            if (!Number.isFinite(opt?.value)) return;
            map.set(opt.value, opt);
        });
        return Array.from(map.values());
    };

    const getTreatmentSlipId = (service: any): number | null => {
        const candidate =
            service?.treatment_slip_id ??
            service?.treatment_slip?.id ??
            service?.category?.treatment_slip_id ??
            service?.category?.treatment_slip?.id;

        const parsed = Number(candidate);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const getServiceTreatmentSlipId = (serviceId: number): number | null => {
        const cached = serviceSlipCache.current[serviceId];
        if (Number.isFinite(cached)) return cached;

        const fromAppointment = (appointment?.services || []).find((s: any) => Number(s?.id) === Number(serviceId));
        const slipId = getTreatmentSlipId(fromAppointment);
        if (slipId !== null) {
            serviceSlipCache.current[serviceId] = slipId;
        }
        return slipId;
    };

    const steps = [
        { key: "new", value: 1, title: "Đặt lịch mới", color: "#3b82f6" },
        { key: "confirmed", value: 2, title: "Đã xác nhận", color: "#13c2c2" },
        { key: "pending", value: 3, title: "Chờ thực hiện", color: "#f59e0b" },
        { key: "done", value: 4, title: "Đã thực hiện", color: "#52c41a" },
        { key: "cancelled", value: 0, title: "Hủy lịch", color: "#ef4444" },
    ];
    const countries = [
        { value: "vn", label: "Việt Nam" },
    ];

    const sourceOptions = [
        { label: "INBOX", value: "INBOX" },
        { label: "COMMENT", value: "COMMENT" },
    ];

    const appointmentStatusOptions = [
        { value: 1, label: "Đặt lịch mới", color: "blue" },
        { value: 2, label: "Đã xác nhận", color: "cyan" },
        { value: 3, label: "Chờ thực hiện", color: "gold" },
        { value: 4, label: "Đã thực hiện", color: "green" },
        { value: 0, label: "Hủy lịch", color: "red" },
    ];

    const statusColor: any = appointmentStatusOptions.reduce((acc, item) => {
        acc[item.label] = item.color;
        return acc;
    }, {} as Record<string, string>);

    const sourceColor: any = {
        "INBOX": "blue",
        "COMMENT": "green",
    };

    const getRandomColor = () => {
        const colors = [
            "#f56a00",
            "#7265e6",
            "#ffbf00",
            "#00a2ae",
            "#52c41a",
            "#eb2f96",
            "#fa541c",
            "#13c2c2",
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const initialFormState: CustomerFormState = {
        name: "",
        phone: "",
        email: "",
        pancake: "",
        pageStaffId: null,
        telesaleId: null,
        branchStaffId: null,
        actionStaffId: null,
        source: "",
        gender: "",
        birthday: "",
        leadSource: "",
        serviceIds: [],
        status: 1,
        customerTagId: null,
        address: "",
        country: "vn",
        city: "",
        ward: "",
    };

    const [data, setData] = useState<CustomerFormState>(initialFormState);


    const leadSourceOptions = [
        {
            label: "Facebook",
            value: "fb",
            icon: <FacebookFilled style={{ color: "#1877F2" }} />,
        },
        {
            label: "Google",
            value: "gg",
            icon: <GoogleOutlined style={{ color: "#EA4335" }} />,
        },
    ];

    const [form, setForm] = useState<CustomerFormState>(initialFormState);
    const selectedServiceValue = useMemo(
        () => form.serviceIds.map((sid) => ({ value: sid, label: getServiceName(sid) })),
        [form.serviceIds, serviceOptions]
    );
    const getActiveKey = () => steps.find((s) => s.value === form.status)?.key;

    const {
        provinceOptions,
        wardOptions,
        loadingProvinces,
        loadingWards,
        loadWards,
        setWardOptions,
    } = useLocationData(form.city || undefined);

    const getLabel = (options: any[], value: any) => {
        return options.find((o) => o.value === value)?.label || "";
    };

    const getLocationLabel = (options: { value: string; label: string }[], value: string) => {
        return options.find((option) => option.value === value)?.label || value || "";
    };

    const mapGenderLabel = (gender?: number) => {
        if (gender === 1) return "Nam";
        if (gender === 2) return "Nữ";
        return "Khác";
    };

    const getStatusLabel = (status?: number) => {
        return appointmentStatusOptions.find((item) => item.value === status)?.label || "Đặt lịch mới";
    };

    const getStatusColor = (status?: number) => {
        return appointmentStatusOptions.find((item) => item.value === status)?.color || "blue";
    };

    const toNullableNumber = (value: any): number | null => {
        if (value === null || value === undefined || value === "") return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const mapAppointmentToForm = (item: AppointmentItem): CustomerFormState => {
        const source = sourceOptions.some((s) => s.value === item.channel_connection_id)
            ? String(item.channel_connection_id)
            : "INBOX";

        const telesaleId = toNullableNumber((item as any).telesale_id);
        const branchStaffId = toNullableNumber((item as any).branch_staff_id);
        const actionStaffId = toNullableNumber((item as any).action_staff_id);

        return {
            name: item.customer_name || item.channel_customer?.display_name || "",
            phone: item.customer_phone || item.channel_customer?.phone || "",
            email: item.customer_email || item.channel_customer?.email || "",
            pancake: "",
            pageStaffId: toNullableNumber(item.staff_id ?? item.staff?.id),
            telesaleId,
            branchStaffId,
            actionStaffId,
            source,
            gender: mapGenderLabel(item.customer_gender ?? item.channel_customer?.gender),
            birthday: item.customer_dob || item.channel_customer?.birthday || "",
            leadSource: "",
            serviceIds: Array.from(new Set((item.services || []).map((s) => s.id))),
            status: typeof item.status === "number" ? item.status : 1,
            customerTagId: (item as any)?.customer_tag_id ?? item.channel_customer?.customer_tag_id ?? null,
            address: (item as any).address || item.channel_customer?.address || "",
            country: "vn",
            city: (item as any).province_code || item.channel_customer?.province_code || "",
            ward: (item as any).ward_code || item.channel_customer?.ward_code || "",
        };
    };

    const getStaffNameById = (list: StaffOption[], staffId: number | null) => {
        if (!staffId) return "";
        return list.find((x) => x.value === staffId)?.label || "";
    };

    const mapGenderToNum = (g: string): number => {
        if (g === "Nam") return 1;
        if (g === "Nữ") return 2;
        return 3;
    };

    const fetchServices = useCallback(async (search: string, page: number, append = false) => {
        setServiceLoading(true);
        try {
            const res = await mixGetV1('/api/v1/be/services', {
                take: 15,
                page,
                ...(search ? { search } : {}),
            });
            if (res?.status) {
                const items: any[] = Array.isArray(res.data?.items)
                    ? res.data.items
                    : Array.isArray(res.data)
                        ? res.data
                        : [];
                const opts = items.map((s: any) => {
                    const slipId = getTreatmentSlipId(s);
                    if (slipId !== null) {
                        serviceSlipCache.current[Number(s.id)] = slipId;
                    }
                    return {
                        value: Number(s.id),
                        label: resolveServiceLabel(s),
                        treatmentSlipId: slipId,
                    };
                });
                setServiceOptions((prev) => dedupeServiceOptions(append ? [...prev, ...opts] : opts));
                const total = Number(res.data?.total || 0);
                setServiceHasMore(page * 15 < total);
            }
        } finally {
            setServiceLoading(false);
        }
    }, []);

    const fetchStaffOptions = useCallback(async () => {
        setLoadingStaffOptions(true);
        try {
            const toOption = (staff: any): StaffOption => ({
                value: Number(staff.id),
                label: staff?.name || staff?.user?.fullname || `#${staff.id}`,
            });

            const toOptions = (res: any) => {
                const items = Array.isArray(res?.data?.items)
                    ? res.data.items
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];
                return items.map(toOption);
            };

            const [pageRes, telesaleRes, branchRes, actionRes] = await Promise.all([
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 2 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 3 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 4 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 5 }).catch(() => null),
            ]);

            setPageStaffOptions(toOptions(pageRes));
            setTelesaleOptions(toOptions(telesaleRes));
            setBranchStaffOptions(toOptions(branchRes));
            setActionStaffOptions(toOptions(actionRes));
        } catch (error) {
            message.error('Không tải được danh sách nhân viên');
        } finally {
            setLoadingStaffOptions(false);
        }
    }, []);

    const saveStaffField = async (field: string, value: number | null) => {
        if (!id || !appointment) return;
        setSavingStaffField(true);
        try {
            const res = await mixPutV1(`/api/v1/be/appointments/${id}`, {
                customer_id: (appointment as any).customer_id ?? appointment.channel_customer_id,
                [field]: value,
            });
            if (!res?.status) throw new Error(res?.msg || 'Cập nhật thất bại');
            setData((prev) => ({
                ...prev,
                ...(field === 'staff_id' ? { pageStaffId: value } : {}),
                ...(field === 'telesale_id' ? { telesaleId: value } : {}),
                ...(field === 'branch_staff_id' ? { branchStaffId: value } : {}),
                ...(field === 'action_staff_id' ? { actionStaffId: value } : {}),
            }));
            setForm((prev) => ({
                ...prev,
                ...(field === 'staff_id' ? { pageStaffId: value } : {}),
                ...(field === 'telesale_id' ? { telesaleId: value } : {}),
                ...(field === 'branch_staff_id' ? { branchStaffId: value } : {}),
                ...(field === 'action_staff_id' ? { actionStaffId: value } : {}),
            }));
            if (field === 'branch_staff_id') {
                const branchStaff = branchStaffOptions.find((o) => o.value === value);
                setAppointment((prev) => prev ? { ...prev, branch_staff: branchStaff ? { name: branchStaff.label } : null } as any : prev);
            }
            if (field === 'action_staff_id') {
                setAppointment((prev) => prev ? { ...prev, action_staff_id: value } as any : prev);
            }
            setEditingStaffField(null);
            message.success('Cập nhật thành công');
        } catch (e: any) {
            message.error(e?.message || 'Cập nhật thất bại');
        } finally {
            setSavingStaffField(false);
        }
    };

    const saveSimpleField = async (field: string, value: any) => {
        if (!id || !appointment) return;
        setSavingSimpleField(true);
        try {
            const apiValue = field === 'customer_gender' ? mapGenderToNum(value) :
                field === 'customer_dob' ? (value || undefined) : value;
            const res = await mixPutV1(`/api/v1/be/appointments/${id}`, {
                customer_id: (appointment as any).customer_id ?? appointment.channel_customer_id,
                [field]: apiValue,
            });
            if (!res?.status) throw new Error(res?.msg || 'Cập nhật thất bại');
            setData((prev) => ({
                ...prev,
                ...(field === 'customer_name' ? { name: value } : {}),
                ...(field === 'customer_email' ? { email: value } : {}),
                ...(field === 'customer_phone' ? { phone: value } : {}),
                ...(field === 'customer_gender' ? { gender: value } : {}),
                ...(field === 'customer_dob' ? { birthday: value } : {}),
            }));
            setForm((prev) => ({
                ...prev,
                ...(field === 'customer_name' ? { name: value } : {}),
                ...(field === 'customer_email' ? { email: value } : {}),
                ...(field === 'customer_phone' ? { phone: value } : {}),
                ...(field === 'customer_gender' ? { gender: value } : {}),
                ...(field === 'customer_dob' ? { birthday: value } : {}),
            }));
            setEditingSimpleField(null);
            message.success('Cập nhật thành công');
        } catch (e: any) {
            message.error(e?.message || 'Cập nhật thất bại');
        } finally {
            setSavingSimpleField(false);
        }
    };

    const saveServicesField = async (serviceIds: number[]) => {
        if (!id || !appointment) return;
        setSavingServices(true);
        try {
            const res = await mixPutV1(`/api/v1/be/appointments/${id}`, {
                customer_id: (appointment as any).customer_id ?? appointment.channel_customer_id,
                service_ids: serviceIds,
            });
            if (!res?.status) throw new Error(res?.msg || 'Cập nhật thất bại');
            setData((prev) => ({ ...prev, serviceIds }));
            setForm((prev) => ({ ...prev, serviceIds }));
            setEditingServices(false);
            message.success('Cập nhật dịch vụ thành công');
        } catch (e: any) {
            message.error(e?.message || 'Cập nhật thất bại');
        } finally {
            setSavingServices(false);
        }
    };

    const loadAddressModalWards = async (provinceCode: string) => {
        setAddressModalLoadingWards(true);
        setAddressModalWardOptions([]);
        try {
            const items = await getWardsByProvince(provinceCode);
            setAddressModalWardOptions(items.map((w: any) => ({ label: w.full_name || (w.prefix ? `${w.prefix} ${w.name}` : w.name), value: w.code || String(w.id) })));
        } catch {
            setAddressModalWardOptions([]);
        } finally {
            setAddressModalLoadingWards(false);
        }
    };

    const openAddressModal = () => {
        setAddressModalForm({ city: form.city || '', ward: form.ward || '', address: form.address || '' });
        setAddressModalWardOptions(wardOptions);
        setAddressModalOpen(true);
    };

    const saveAddress = async () => {
        if (!appointment?.channel_customer_id) return;
        setSavingAddress(true);
        try {
            const res = await updateChannelCustomer(appointment.channel_customer_id, {
                province_code: addressModalForm.city || null,
                ward_code: addressModalForm.ward || null,
                address: addressModalForm.address,
            } as any);
            if (!res?.status) throw new Error(res?.msg || 'Cập nhật thất bại');
            const newCity = addressModalForm.city;
            const newWard = addressModalForm.ward;
            const newAddress = addressModalForm.address;
            setForm((prev) => ({ ...prev, city: newCity, ward: newWard, address: newAddress }));
            setData((prev) => ({ ...prev, city: newCity, ward: newWard, address: newAddress }));
            if (newCity && newCity !== form.city) {
                loadWards(newCity);
                setWardOptions(addressModalWardOptions);
            }
            message.success('Cập nhật địa chỉ thành công');
            setAddressModalOpen(false);
        } catch (e: any) {
            message.error(e?.message || 'Cập nhật thất bại');
        } finally {
            setSavingAddress(false);
        }
    };

    const fetchAppointment = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        const res = await getAppointmentConsultationDetail(Number(id));
        if (res.status && res.data) {
            setAppointment(res.data);
            const mapped = mapAppointmentToForm(res.data);
            setData(mapped);
            setForm(mapped);
            if (mapped.city) {
                loadWards(mapped.city);
            }
            // Pre-seed service options with the services already on this appointment
            if (res.data.services?.length) {
                setServiceOptions((prev) => {
                    const preloaded = res.data!.services.map((s) => {
                        const slipId = getTreatmentSlipId(s);
                        if (slipId !== null) {
                            serviceSlipCache.current[s.id] = slipId;
                        }
                        return { value: s.id, label: resolveServiceLabel(s), treatmentSlipId: slipId };
                    });
                    return dedupeServiceOptions([...prev, ...preloaded]);
                });
            }
            // Seed cache directly so names survive any later dropdown resets
            res.data.services?.forEach((s) => {
                serviceNameCache.current[s.id] = resolveServiceLabel(s);
                const slipId = getTreatmentSlipId(s);
                if (slipId !== null) {
                    serviceSlipCache.current[s.id] = slipId;
                }
            });
        } else {
            message.error(res.msg || "Không tải được chi tiết lịch hẹn");
        }
        setLoading(false);
    }, [id, loadWards]);

    useEffect(() => {
        fetchAppointment();
    }, [fetchAppointment]);

    // Lưu danh sách dịch vụ (thêm/xóa) -> PUT service_ids (sync) rồi refetch
    const handleSaveServiceIds = async (serviceIds: number[]): Promise<boolean> => {
        if (!id) return false;
        try {
            const res = await mixPutV1(`/api/v1/be/appointments/${id}`, {
                customer_id: (appointment as any)?.customer_id ?? appointment?.channel_customer_id,
                service_ids: serviceIds,
            });
            if (!res?.status) throw new Error(res?.msg || 'Cập nhật thất bại');
            await fetchAppointment();
            message.success('Cập nhật dịch vụ thành công');
            return true;
        } catch (e: any) {
            message.error(e?.message || 'Cập nhật thất bại');
            return false;
        }
    };

    useEffect(() => {
        fetchStaffOptions();
    }, [fetchStaffOptions]);

    // Initial service list load
    useEffect(() => {
        fetchServices("", 1, false);
    }, [fetchServices]);

    // Accumulate names into cache whenever list changes
    useEffect(() => {
        serviceOptions.forEach((o) => {
            serviceNameCache.current[o.value] = o.label;
            if (Number.isFinite(o.treatmentSlipId as number)) {
                serviceSlipCache.current[o.value] = Number(o.treatmentSlipId);
            }
        });
    }, [serviceOptions]);

    // Re-fetch when debounced keyword changes
    useEffect(() => {
        setServicePage(1);
        fetchServices(debouncedServiceKw, 1, false);
    }, [debouncedServiceKw]);

    // Load more page
    useEffect(() => {
        if (servicePage > 1) {
            fetchServices(debouncedServiceKw, servicePage, true);
        }
    }, [servicePage]);

    const save = async () => {
        if (!id || !appointment) return;

        const changes: any[] = [];

        // 🔥 so sánh từng field
        if (form.name !== data.name) {
            changes.push({
                field: "name",
                from: data.name,
                to: form.name,
            });
        }

        if (form.phone !== data.phone) {
            changes.push({
                field: "phone",
                from: data.phone,
                to: form.phone,
            });
        }

        if (form.email !== data.email) {
            changes.push({
                field: "email",
                from: data.email,
                to: form.email,
            });
        }

        if (isAdmin && form.pageStaffId !== data.pageStaffId) {
            changes.push({
                field: "staff_id",
                from: getStaffNameById(pageStaffOptions, data.pageStaffId),
                to: getStaffNameById(pageStaffOptions, form.pageStaffId),
            });
        }

        if (isAdmin && form.telesaleId !== data.telesaleId) {
            changes.push({
                field: "telesale_id",
                from: getStaffNameById(telesaleOptions, data.telesaleId),
                to: getStaffNameById(telesaleOptions, form.telesaleId),
            });
        }

        if (isAdmin && form.branchStaffId !== data.branchStaffId) {
            changes.push({
                field: "branch_staff_id",
                from: getStaffNameById(branchStaffOptions, data.branchStaffId),
                to: getStaffNameById(branchStaffOptions, form.branchStaffId),
            });
        }

        if (form.actionStaffId !== data.actionStaffId) {
            changes.push({
                field: "action_staff_id",
                from: getStaffNameById(actionStaffOptions, data.actionStaffId),
                to: getStaffNameById(actionStaffOptions, form.actionStaffId),
            });
        }

        if (form.status !== data.status) {
            changes.push({
                field: "status",
                from: getStatusLabel(data.status),
                to: getStatusLabel(form.status),
            });
        }

        if (form.customerTagId !== data.customerTagId) {
            const nextTags = getNextTags(appointment);
            const fromTag = nextTags.find((t) => t.id === data.customerTagId);
            const toTag = nextTags.find((t) => t.id === form.customerTagId);
            changes.push({
                field: "customer_tag_id",
                from: fromTag?.name || fromTag?.text || String(data.customerTagId ?? ''),
                to: toTag?.name || toTag?.text || String(form.customerTagId ?? ''),
            });
        }

        if (form.address !== data.address) {
            changes.push({
                field: "address",
                from: data.address,
                to: form.address,
            });
        }

        const hasLocationChanges =
            form.city !== data.city ||
            form.ward !== data.ward ||
            form.address !== data.address;

        setSaving(true);
        try {
            const payload: any = {
                customer_name: form.name,
                customer_phone: form.phone,
                customer_email: form.email,
                customer_gender: mapGenderToNum(form.gender),
                customer_dob: form.birthday || undefined,
                customer_id: (appointment as any).customer_id ?? appointment.channel_customer_id,
                service_ids: form.serviceIds,
                status: form.status,
                ...(form.customerTagId !== data.customerTagId && form.customerTagId != null
                    ? { customer_tag_id: form.customerTagId }
                    : {}),
            };

            if (isAdmin) {
                payload.staff_id = form.pageStaffId;
                payload.telesale_id = form.telesaleId;
                payload.branch_staff_id = form.branchStaffId;
            }
            if (isAdmin) payload.action_staff_id = form.actionStaffId;

            const res = await mixPutV1(`/api/v1/be/appointments/${id}`, payload);
            if (!res?.status) {
                throw new Error(res?.msg || 'Cập nhật thất bại');
            }

            if (appointment.channel_customer_id && hasLocationChanges) {
                const customerRes = await updateChannelCustomer(appointment.channel_customer_id, {
                    province_code: form.city || null,
                    ward_code: form.ward || null,
                    address: form.address === "" ? "" : form.address,
                } as any);

                if (!customerRes?.status) {
                    throw new Error('Cập nhật tỉnh thành/phường xã thất bại');
                }
            }

            if (changes.length > 0) {
                addHistory({ changes });
            }

            setData(form);
            setAppointment((prev) => {
                if (!prev) return prev;
                const nextTags = getNextTags(prev);
                const selectedTag = form.customerTagId != null
                    ? nextTags.find((t) => t.id === form.customerTagId)
                    : null;
                return {
                    ...prev,
                    status: form.status,
                    staff_id: form.pageStaffId,
                    ...(form.customerTagId !== data.customerTagId && selectedTag
                        ? {
                            customer_tag_id: selectedTag.id,
                            customer_tag: {
                                ...((prev as any)?.customer_tag || {}),
                                id: selectedTag.id,
                                name: selectedTag.name || '',
                                color: selectedTag.color ?? null,
                                description: selectedTag.description ?? null,
                            },
                            customer_tag_status: selectedTag.status ?? prev.customer_tag_status,
                            customer_tag_status_text: selectedTag.status_text ?? selectedTag.customer_tag_status_text ?? selectedTag.name ?? prev.customer_tag_status_text,
                        }
                        : {}),
                    services: form.serviceIds.map((sid) => {
                        const current = (prev.services || []).find((s: any) => Number(s?.id) === Number(sid));
                        return {
                            ...(current || {}),
                            id: sid,
                            name: current?.name || getServiceName(sid),
                            treatment_slip_id: getServiceTreatmentSlipId(sid),
                        } as any;
                    }),
                    channel_customer: prev.channel_customer
                        ? {
                            ...prev.channel_customer,
                            address: form.address,
                            province_code: form.city || null,
                            ward_code: form.ward || null,
                            ...(form.customerTagId !== data.customerTagId && selectedTag
                                ? {
                                    customer_tag_id: selectedTag.id,
                                    customer_tag: {
                                        ...(prev as any)?.customer_tag,
                                        ...prev.channel_customer?.customer_tag,
                                        id: selectedTag.id,
                                        name: selectedTag.name || (prev as any)?.customer_tag?.name || prev.channel_customer?.customer_tag?.name || '',
                                        color: selectedTag.color ?? (prev as any)?.customer_tag?.color ?? prev.channel_customer?.customer_tag?.color,
                                        description: selectedTag.description ?? (prev as any)?.customer_tag?.description ?? prev.channel_customer?.customer_tag?.description ?? null,
                                    },
                                }
                                : {}),
                        }
                        : prev.channel_customer,
                    ...(form.telesaleId !== undefined ? { telesale_id: form.telesaleId } : {}),
                    ...(form.branchStaffId !== undefined ? { branch_staff_id: form.branchStaffId } : {}),
                    ...(form.actionStaffId !== undefined ? { action_staff_id: form.actionStaffId } : {}),
                };
            });
            // Merge any newly-selected service names into option list so they display on next render
            const selectedOpts = form.serviceIds
                .map((sid) => serviceOptions.find((o) => o.value === sid))
                .filter(Boolean) as Array<{ value: number; label: string; treatmentSlipId?: number | null }>;
            setServiceOptions((prev) => {
                const ids = new Set(prev.map((o) => o.value));
                return dedupeServiceOptions([...prev, ...selectedOpts.filter((o) => !ids.has(o.value))]);
            });
            setEditing(false);
            message.success('Cập nhật lịch hẹn thành công');
            // Refetch silently to get fresh data from server
            getAppointmentConsultationDetail(Number(id)).then((fresh) => {
                if (fresh.status && fresh.data) {
                    setAppointment(fresh.data);
                }
            });
        } catch (error: any) {
            fetchAppointment();
            message.error(error?.message || 'Cập nhật lịch hẹn thất bại');
        } finally {
            setSaving(false);
        }
    };

    const addHistory = (content: any) => {
        const now = new Date();

        setHistory((prev) => [
            {
                id: Date.now(),
                time: dayjs(now).format("HH:mm DD/MM/YYYY"),
                user: "Phan",
                ...content,
            },
            ...prev,
        ]);
    };

    const relatedTreatmentSlipIds = useMemo(() => {
        const ids = new Set<number>();
        const sourceServiceIds = editing
            ? form.serviceIds
            : (appointment?.services || []).map((service: any) => Number(service?.id));

        sourceServiceIds.forEach((sid) => {
            const slipId = getServiceTreatmentSlipId(Number(sid));
            if (slipId !== null) ids.add(slipId);
        });
        return ids;
    }, [appointment, editing, form.serviceIds]);

    const specialtyTabs = useMemo(() => {
        const defs = [
            { key: "hair_clinic", slipId: 1, label: t('sidebar.hair_clinic') },
            { key: "miko_reception", slipId: 2, label: t('sidebar.miko_reception') },
            { key: "cosmetic_surgery", slipId: 3, label: t('sidebar.cosmetic_surgery') },
            { key: "thread_lift", slipId: 4, label: t('sidebar.thread_lift') },
            { key: "consultation_form", slipId: 5, label: t('sidebar.ozempic') },
            { key: "beauty_consultation", slipId: 6, label: t('sidebar.beauty_consultation') },
            { key: "skin_consultation", slipId: 7, label: t('sidebar.skin_consultation') },
        ];

        return defs.filter((tab) => relatedTreatmentSlipIds.has(tab.slipId));
    }, [relatedTreatmentSlipIds, t]);

    const tabItems = useMemo(() => {
        return [
            {
                key: "detail",
                label: appointment?.code ? `Thông tin ${appointment.code}` : "Thông tin",
            },
            ...(canViewHistory ? [{
                key: "history",
                label: "Lịch sử",
            }] : []),
            ...specialtyTabs,
        ];
    }, [appointment?.code, history, specialtyTabs, statusColor, canViewHistory]);

    useEffect(() => {
        if (!tabItems.some((item) => item.key === activeTab)) {
            setActiveTab("detail");
        }
    }, [activeTab, tabItems]);

    return (
        <div className="detail-lichhen-page">
            <Layout>
                <Helmet>
                    <title>{t('page.order_details')} | Khothi</title>
                </Helmet>
                <Spin spinning={loading}>
                    <div className="detail-lichhen-head dark:bg-[#1f1f1f]">
                        <div className="flex items-center gap-2 text-sm">
                            <Link to="/consultations" className="flex items-center gap-2">
                                <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                            </Link>
                            <Link to="/consultations" className="flex items-center gap-2 text-000000e0">
                                <span>{t('sidebar.consultation')}</span>
                            </Link>
                            <span><RightOutlined className="w-4 h-4 text-[12px] text-gray-500 text-000000e0" /></span>
                            <span className="text-000000e0">{appointment?.customer_name || form.name || t('page.order_details')}</span>
                        </div>
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={[
                                {
                                    key: "detail",
                                    label: appointment?.code ? `Thông tin ${appointment.code}` : "Thông tin",
                                },
                                {
                                    key: "history",
                                    label: "Lịch sử chăm sóc",
                                },
                            ]}
                        />
                    </div>
                    <div className="detail-lichhen-mainx">
                        {activeTab === "detail" && (
                            <div className="info-section">
                                <div className="info-section-right overflow-hidden">
                                    <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6 mb-6 text-000000e0">
                                        <div className="service-info">
                                            <div className="title-info gap-2 text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                                                <Users
                                                    size={16}
                                                    className="text-[#4e91ab]"
                                                />
                                                1. Nhân sự phụ trách lịch hẹn
                                            </div>
                                            <div className="service-info-item-2">
                                                <div className="bg-slate-50/50 dark:bg-[#161515] rounded-lg p-3.5 border border-slate-100 hover:border-primary-200 transition">
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Trực page</label>
                                                    {editingStaffField === 'staff_id' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <Select
                                                                className="flex-1"
                                                                value={editingStaffValue ?? undefined}
                                                                onChange={(v) => setEditingStaffValue(v ?? null)}
                                                                options={pageStaffOptions}
                                                                loading={loadingStaffOptions}
                                                                allowClear
                                                                autoFocus
                                                                showSearch
                                                                filterOption={filterOptionVietnamese}
                                                            />
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                icon={savingStaffField ? <LoadingOutlined /> : <CheckOutlined />}
                                                                onClick={() => saveStaffField('staff_id', editingStaffValue)}
                                                                disabled={savingStaffField}
                                                            />
                                                            <Button size="small" disabled={savingStaffField} onClick={() => setEditingStaffField(null)}>Hủy</Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group">
                                                            <span>{getStaffNameById(pageStaffOptions, data.pageStaffId) || '—'}</span>
                                                            {isAdmin && (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />}
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={() => { setEditingStaffField('staff_id'); setEditingStaffValue(data.pageStaffId); }}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="bg-slate-50/50 dark:bg-[#161515] rounded-lg p-3.5 border border-slate-100 hover:border-primary-200 transition">
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Telesale</label>
                                                    {editingStaffField === 'telesale_id' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <Select
                                                                className="flex-1"
                                                                value={editingStaffValue ?? undefined}
                                                                onChange={(v) => setEditingStaffValue(v ?? null)}
                                                                options={telesaleOptions}
                                                                loading={loadingStaffOptions}
                                                                allowClear
                                                                autoFocus
                                                                showSearch
                                                                filterOption={filterOptionVietnamese}
                                                            />
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                icon={savingStaffField ? <LoadingOutlined /> : <CheckOutlined />}
                                                                onClick={() => saveStaffField('telesale_id', editingStaffValue)}
                                                                disabled={savingStaffField}
                                                            />
                                                            <Button size="small" disabled={savingStaffField} onClick={() => setEditingStaffField(null)}>Hủy</Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group">
                                                            <span>{getStaffNameById(telesaleOptions, data.telesaleId) || '—'}</span>
                                                            {isAdmin && (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />}
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={() => { setEditingStaffField('telesale_id'); setEditingStaffValue(data.telesaleId); }}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="bg-slate-50/50 dark:bg-[#161515] rounded-lg p-3.5 border border-slate-100 hover:border-primary-200 transition">
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Sale cơ sở</label>
                                                    {editingStaffField === 'branch_staff_id' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <Select
                                                                className="flex-1"
                                                                value={editingStaffValue ?? undefined}
                                                                onChange={(v) => setEditingStaffValue(v ?? null)}
                                                                options={branchStaffOptions}
                                                                loading={loadingStaffOptions}
                                                                allowClear
                                                                autoFocus
                                                                showSearch
                                                                filterOption={filterOptionVietnamese}
                                                            />
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                icon={savingStaffField ? <LoadingOutlined /> : <CheckOutlined />}
                                                                onClick={() => saveStaffField('branch_staff_id', editingStaffValue)}
                                                                disabled={savingStaffField}
                                                            />
                                                            <Button size="small" disabled={savingStaffField} onClick={() => setEditingStaffField(null)}>Hủy</Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group">
                                                            <span>{(appointment as any)?.branch_staff?.name || getStaffNameById(branchStaffOptions, data.branchStaffId) || '—'}</span>
                                                            {isAdmin && (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />}
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={() => { setEditingStaffField('branch_staff_id'); setEditingStaffValue(data.branchStaffId); }}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="bg-slate-50/50 dark:bg-[#161515] rounded-lg p-3.5 border border-slate-100 hover:border-primary-200 transition">
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Thực hiện</label>
                                                    {editingStaffField === 'action_staff_id' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <Select
                                                                className="flex-1"
                                                                value={editingStaffValue ?? undefined}
                                                                onChange={(v) => setEditingStaffValue(v ?? null)}
                                                                options={actionStaffOptions}
                                                                loading={loadingStaffOptions}
                                                                allowClear
                                                                autoFocus
                                                                showSearch
                                                                filterOption={filterOptionVietnamese}
                                                            />
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                icon={savingStaffField ? <LoadingOutlined /> : <CheckOutlined />}
                                                                onClick={() => saveStaffField('action_staff_id', editingStaffValue)}
                                                                disabled={savingStaffField}
                                                            />
                                                            <Button size="small" disabled={savingStaffField} onClick={() => setEditingStaffField(null)}>Hủy</Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group">
                                                            <span>{getStaffNameById(actionStaffOptions, data.actionStaffId) || '—'}</span>
                                                            {canEditActionStaff && (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />}
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={() => { setEditingStaffField('action_staff_id'); setEditingStaffValue(data.actionStaffId); }}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6 mb-6 text-000000e0">
                                        <div className="service-info">
                                            <div className="title-info gap-2 text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                                                <CircleDashed
                                                    size={16}
                                                    className="text-[#4e91ab]"
                                                />
                                                2. Trạng Thái
                                            </div>
                                            {/* <div className="min-w-0">
                                                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Dịch vụ quan tâm</label>
                                                {editingServices ? (
                                                    <div className="flex flex-col gap-1.5">
                                                        <Select
                                                            mode="multiple"
                                                            labelInValue
                                                            className="w-full"
                                                            placeholder="Tìm và chọn dịch vụ..."
                                                            value={editingServicesValue.map((sid) => ({ value: sid, label: getServiceName(sid) }))}
                                                            onChange={(v: any[], optionList: any) => {
                                                                const ids = (v || []).map((item) => Number(item?.value)).filter((item) => Number.isFinite(item));
                                                                (v || []).forEach((item: any) => {
                                                                    if (Number.isFinite(Number(item?.value)) && item?.label) {
                                                                        serviceNameCache.current[Number(item.value)] = String(item.label);
                                                                    }
                                                                });
                                                                const normalizedOptionList = Array.isArray(optionList) ? optionList : [];
                                                                normalizedOptionList.forEach((opt: any) => {
                                                                    const sid = Number(opt?.value);
                                                                    const slipId = Number(opt?.treatmentSlipId);
                                                                    if (Number.isFinite(sid) && Number.isFinite(slipId)) {
                                                                        serviceSlipCache.current[sid] = slipId;
                                                                    }
                                                                });
                                                                setEditingServicesValue(ids);
                                                            }}
                                                            showSearch
                                                            filterOption={false}
                                                            onSearch={(val) => setServiceSearchKw(val)}
                                                            loading={serviceLoading}
                                                            maxTagCount="responsive"
                                                            maxTagTextLength={30}
                                                            notFoundContent={
                                                                serviceLoading
                                                                    ? <div className="flex justify-center py-2"><Spin size="small" /></div>
                                                                    : <span className="text-gray-400 text-sm">Không tìm thấy</span>
                                                            }
                                                            options={serviceOptions}
                                                            dropdownRender={(menu) => (
                                                                <>
                                                                    {menu}
                                                                    {serviceHasMore && (
                                                                        <div className="flex justify-center py-2 border-t">
                                                                            <Button
                                                                                size="small"
                                                                                type="link"
                                                                                loading={serviceLoading}
                                                                                onClick={() => setServicePage((p) => p + 1)}
                                                                            >
                                                                                Tải thêm
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            autoFocus
                                                        />
                                                        <div className="flex gap-1.5">
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                icon={savingServices ? <LoadingOutlined /> : <CheckOutlined />}
                                                                onClick={() => saveServicesField(editingServicesValue)}
                                                                disabled={savingServices}
                                                            >
                                                                Lưu
                                                            </Button>
                                                            <Button size="small" disabled={savingServices} onClick={() => setEditingServices(false)}>Hủy</Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex max-w-full flex-wrap gap-1.5 group items-start">
                                                        {data.serviceIds?.length ? (
                                                            data.serviceIds.map((sid) => (
                                                                <Tag key={sid} className="m-0 max-w-full">
                                                                    <span className="block max-w-[360px] truncate" title={getServiceName(sid)}>
                                                                        {getServiceName(sid)}
                                                                    </span>
                                                                </Tag>
                                                            ))
                                                        ) : (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                        {canEdit && (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />}
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                                onClick={() => {
                                                                    setEditingServicesValue([...data.serviceIds]);
                                                                    setEditingServices(true);
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div> */}
                                            <div className="min-w-0">
                                                {/* <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Trạng thái</label> */}
                                                {editing ? (
                                                    <Select
                                                        value={form.customerTagId ?? undefined}
                                                        onChange={(v) => setForm({ ...form, customerTagId: v ?? null })}
                                                        options={(() => {
                                                            const nextTags = getNextTags(appointment);
                                                            const opts = nextTags.map((tag) => ({
                                                                value: tag.id,
                                                                label: tag.name || tag.text || String(tag.id),
                                                                color: tag.color,
                                                            }));
                                                            const currentTagId = (appointment as any)?.customer_tag_id ?? appointment?.channel_customer?.customer_tag_id;
                                                            const currentTag = (appointment as any)?.customer_tag ?? appointment?.channel_customer?.customer_tag;
                                                            if (currentTagId != null && !opts.some((o) => o.value === currentTagId)) {
                                                                opts.unshift({
                                                                    value: currentTagId,
                                                                    label: currentTag?.name || currentTag?.text || String(currentTagId),
                                                                    color: currentTag?.color || null,
                                                                });
                                                            }
                                                            return opts;
                                                        })()}
                                                        optionRender={(option) => (
                                                            <Tag color={(option.data as any).color || undefined}>{option.label}</Tag>
                                                        )}
                                                        className="w-full"
                                                        allowClear
                                                        placeholder="Chọn trạng thái"
                                                    />
                                                ) : (
                                                    (() => {
                                                        const nextTags = getNextTags(appointment);
                                                        const tagOpts = nextTags.map((tag) => ({
                                                            value: tag.id,
                                                            label: tag.name || tag.text || String(tag.id),
                                                            color: tag.color || "default",
                                                        }));
                                                        const currentTagId = (appointment as any)?.customer_tag_id ?? appointment?.channel_customer?.customer_tag_id;
                                                        const currentTag = (appointment as any)?.customer_tag ?? appointment?.channel_customer?.customer_tag;
                                                        if (currentTagId != null && !tagOpts.some((o) => o.value === currentTagId)) {
                                                            tagOpts.unshift({
                                                                value: currentTagId,
                                                                label: currentTag?.name || currentTag?.text || String(currentTagId),
                                                                color: currentTag?.color || "default",
                                                            });
                                                        }
                                                        const hasTags = !!currentTagId;
                                                        const activeTagOpt = tagOpts.find((o) => o.value === currentTagId);
                                                        const fallbackTag = !activeTagOpt && currentTag ? currentTag : null;
                                                        const statusText = appointment?.customer_tag_status_text;
                                                        const tagStatusNum = appointment?.customer_tag_status;
                                                        const desc = currentTag?.description;

                                                        const handleSelectTag = (val: number) => {
                                                            const selTag = nextTags.find((t) => t.id === val);
                                                            const newLabel = selTag?.name || selTag?.text || String(val);
                                                            Modal.confirm({
                                                                title: 'Xác nhận thay đổi trạng thái',
                                                                content: `Bạn có chắc muốn đổi trạng thái sang "${newLabel}"?`,
                                                                okText: 'Xác nhận',
                                                                cancelText: 'Hủy',
                                                                onOk: async () => {
                                                                    setUpdatingTagField('customer_tag_id');
                                                                    try {
                                                                        const res = await mixPutV1(`/api/v1/be/appointments/${id}`, {
                                                                            customer_id: (appointment as any).customer_id ?? appointment?.channel_customer_id,
                                                                            customer_tag_id: val,
                                                                        });
                                                                        if (!res?.status) throw new Error(res?.msg || 'Cập nhật thất bại');
                                                                        setAppointment((prev) => {
                                                                            if (!prev) return prev;
                                                                            return {
                                                                                ...prev,
                                                                                customer_tag_id: val,
                                                                                customer_tag_status: selTag?.status ?? prev.customer_tag_status,
                                                                                customer_tag_status_text: selTag?.status_text ?? selTag?.name ?? prev.customer_tag_status_text,
                                                                                customer_tag: selTag ? { id: selTag.id, name: selTag.name || '', color: selTag.color ?? null, description: selTag.description ?? null } : prev.customer_tag,
                                                                            };
                                                                        });
                                                                        message.success('Cập nhật trạng thái thành công');
                                                                        // Thẻ mới có thể thuộc màn hình khác (trường page: 1=lịch hẹn,
                                                                        // 2=lịch tư vấn, 3=lịch thực hiện) -> chuyển sang đúng trang chi tiết
                                                                        const nextPath = getAppointmentDetailPath(selTag?.page, String(id));
                                                                        if (nextPath && Number(selTag?.page) !== CURRENT_PAGE) {
                                                                            navigate(nextPath, { replace: true });
                                                                            return;
                                                                        }
                                                                        fetchAppointment();
                                                                    } catch (e: any) {
                                                                        message.error(e?.message || 'Cập nhật thất bại');
                                                                    } finally {
                                                                        setUpdatingTagField(null);
                                                                    }
                                                                },
                                                            });
                                                        };

                                                        return (
                                                            <div className="flex flex-col gap-1.5 mt-1">
                                                                <div className="flex gap-1.5 mt-1">
                                                                    {statusText && (
                                                                        <Tag color={getStatusColor(tagStatusNum ?? undefined)}>{statusText}</Tag>
                                                                    )}
                                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                                        {fallbackTag ? (
                                                                            <Tag color={fallbackTag.color || "default"}>{fallbackTag.name || "—"}</Tag>
                                                                        ) : activeTagOpt ? (
                                                                            <Tag color={activeTagOpt.color || "default"}>{activeTagOpt.label}</Tag>
                                                                        ) : !hasTags ? (
                                                                            <span className="text-gray-400 text-sm">—</span>
                                                                        ) : null}
                                                                        {canChangeStatus && tagOpts.length > 0 && !(tagOpts.length === 1 && tagOpts[0].value === currentTagId) && (
                                                                            <Dropdown
                                                                                trigger={["click"]}
                                                                                disabled={updatingTagField === 'customer_tag_id'}
                                                                                menu={{
                                                                                    items: tagOpts.map((opt) => ({
                                                                                        key: opt.value,
                                                                                        label: (
                                                                                            <div className="flex items-center justify-between min-w-[140px]">
                                                                                                <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                                                                                {currentTagId === opt.value && (
                                                                                                    <CheckOutlined className="text-green-500 ml-2" />
                                                                                                )}
                                                                                            </div>
                                                                                        ),
                                                                                        onClick: () => handleSelectTag(opt.value),
                                                                                    })),
                                                                                }}
                                                                            >
                                                                                <Button
                                                                                    type="text"
                                                                                    size="small"
                                                                                    icon={updatingTagField === 'customer_tag_id' ? <LoadingOutlined /> : <SyncOutlined className="text-gray-400 hover:text-blue-500" />}
                                                                                />
                                                                            </Dropdown>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {desc && (
                                                                    <div className="mt-2 space-y-0.5 desc-info-status">
                                                                        {desc.split('\n').filter((l: string) => l.trim()).map((line: string, i: number) => {
                                                                            const clean = line.replace(/^\*\s*/, '');
                                                                            return (
                                                                                <div key={i} className="flex items-start gap-1 text-xs text-gray-400 leading-snug">
                                                                                    <span>{clean}</span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-6">
                                        <CustomerService services={appointment?.services} onSaveServiceIds={handleSaveServiceIds} canEdit={canEdit} />
                                        <AppointmentPosOrder appointment={appointment} />
                                    </div>
                                    <NoteBox appointmentId={appointment?.id ?? (id ? Number(id) : null)} permissionModule="consultation" />
                                </div>

                                <div className="info-section-left">
                                    <div className="sticky top-[64px]">
                                        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6 mb-6 text-000000e0">
                                            <div className="customer-info">
                                                {/* Avatar header: name + gender tag */}
                                                <div className="flex items-center gap-3 pb-4 mb-2 border-b border-gray-100">
                                                    <Avatar
                                                        size={48}
                                                        style={{ backgroundColor: '#A5E5D3', color: '#0F766E', fontWeight: 600, flexShrink: 0 }}
                                                    >
                                                        {(data.name || appointment?.customer_name || 'NA')
                                                            .split(/\s+/).filter(Boolean).slice(-2)
                                                            .map((s: string) => s[0] || '').join('').toUpperCase() || 'NA'}
                                                    </Avatar>
                                                    <div className="flex-1 min-w-0">
                                                        {editingSimpleField === 'customer_name' ? (
                                                            <div className="flex items-center gap-1.5">
                                                                <Input
                                                                    className="flex-1"
                                                                    value={editingSimpleValue ?? ''}
                                                                    onChange={(e) => setEditingSimpleValue(e.target.value)}
                                                                    autoFocus
                                                                    onPressEnter={() => saveSimpleField('customer_name', editingSimpleValue)}
                                                                />
                                                                <Button type="primary" size="small" icon={savingSimpleField ? <LoadingOutlined /> : <CheckOutlined />} onClick={() => saveSimpleField('customer_name', editingSimpleValue)} disabled={savingSimpleField} />
                                                                <Button size="small" disabled={savingSimpleField} onClick={() => setEditingSimpleField(null)}>Hủy</Button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 group">
                                                                <span className="font-semibold text-base text-gray-800 leading-tight text-000000e0">{data.name || '—'}</span>
                                                                {canEdit && <Button type="text" size="small" icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />} className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditingSimpleField('customer_name'); setEditingSimpleValue(data.name); }} />}
                                                            </div>
                                                        )}
                                                        {editingSimpleField === 'customer_gender' ? (
                                                            <div className="flex items-center gap-1.5 mt-1">
                                                                <Select
                                                                    size="small"
                                                                    className="flex-1"
                                                                    value={editingSimpleValue ?? undefined}
                                                                    onChange={(v) => setEditingSimpleValue(v)}
                                                                    options={[
                                                                        { label: 'Nam', value: 'Nam' },
                                                                        { label: 'Nữ', value: 'Nữ' },
                                                                        { label: 'Khác', value: 'Khác' },
                                                                    ]}
                                                                    autoFocus
                                                                />
                                                                <Button type="primary" size="small" icon={savingSimpleField ? <LoadingOutlined /> : <CheckOutlined />} onClick={() => saveSimpleField('customer_gender', editingSimpleValue)} disabled={savingSimpleField} />
                                                                <Button size="small" disabled={savingSimpleField} onClick={() => setEditingSimpleField(null)}>Hủy</Button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 group mt-1">
                                                                <Tag color="cyan" style={{ marginRight: 0 }}>Khách {data.gender || '—'}</Tag>
                                                                {canEdit && <Button type="text" size="small" icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />} className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditingSimpleField('customer_gender'); setEditingSimpleValue(data.gender); }} />}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Email</label>
                                                    {editingSimpleField === 'customer_email' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <Input
                                                                className="flex-1"
                                                                value={editingSimpleValue ?? ''}
                                                                onChange={(e) => setEditingSimpleValue(e.target.value)}
                                                                autoFocus
                                                                onPressEnter={() => saveSimpleField('customer_email', editingSimpleValue)}
                                                            />
                                                            <Button type="primary" size="small" icon={savingSimpleField ? <LoadingOutlined /> : <CheckOutlined />} onClick={() => saveSimpleField('customer_email', editingSimpleValue)} disabled={savingSimpleField} />
                                                            <Button size="small" disabled={savingSimpleField} onClick={() => setEditingSimpleField(null)}>Hủy</Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group">
                                                            <span>{data.email || '—'}</span>
                                                            {canEdit && <Button type="text" size="small" icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />} className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditingSimpleField('customer_email'); setEditingSimpleValue(data.email); }} />}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Số điện thoại</label>
                                                    {editingSimpleField === 'customer_phone' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <Input
                                                                className="flex-1"
                                                                value={editingSimpleValue ?? ''}
                                                                onChange={(e) => setEditingSimpleValue(e.target.value)}
                                                                autoFocus
                                                                onPressEnter={() => saveSimpleField('customer_phone', editingSimpleValue)}
                                                            />
                                                            <Button type="primary" size="small" icon={savingSimpleField ? <LoadingOutlined /> : <CheckOutlined />} onClick={() => saveSimpleField('customer_phone', editingSimpleValue)} disabled={savingSimpleField} />
                                                            <Button size="small" disabled={savingSimpleField} onClick={() => setEditingSimpleField(null)}>Hủy</Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group">
                                                            <PhoneOutlined />
                                                            <span>{data.phone || '—'}</span>
                                                            {canEdit && <Button type="text" size="small" icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />} className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditingSimpleField('customer_phone'); setEditingSimpleValue(data.phone); }} />}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Ngày sinh</label>
                                                    {editingSimpleField === 'customer_dob' ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <DatePicker
                                                                value={editingSimpleValue ? dayjs(editingSimpleValue) : undefined}
                                                                onChange={(d) => setEditingSimpleValue(d?.format('YYYY-MM-DD') || '')}
                                                                locale={datePickerLocale}
                                                                format={dateDisplayFormat}
                                                                autoFocus
                                                            />
                                                            <Button type="primary" size="small" icon={savingSimpleField ? <LoadingOutlined /> : <CheckOutlined />} onClick={() => saveSimpleField('customer_dob', editingSimpleValue)} disabled={savingSimpleField} />
                                                            <Button size="small" disabled={savingSimpleField} onClick={() => setEditingSimpleField(null)}>Hủy</Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 group">
                                                            <span>{data.birthday ? dayjs(data.birthday).format('DD/MM/YYYY') : '—'}</span>
                                                            {canEdit && <Button type="text" size="small" icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />} className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditingSimpleField('customer_dob'); setEditingSimpleValue(data.birthday); }} />}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Địa chỉ</label>
                                                    <div className="flex items-center gap-2 group">
                                                        <span>{[form.address, getLocationLabel(wardOptions, form.ward), getLocationLabel(provinceOptions, form.city)].filter(Boolean).join(', ') || '—'}</span>
                                                        {canEdit && <Button type="text" size="small" icon={<EditOutlined className="text-gray-400 hover:text-blue-500" />} className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={openAddressModal} />}
                                                    </div>
                                                </div>

                                                <div className="mt-6 pt-4 border-t border-slate-100 dark:border-[#2a2a2a] flex gap-2">

                                                    <a
                                                        href={`tel:${data.phone}`}
                                                        className="
                                                            flex-1
                                                            py-2
                                                            bg-slate-50
                                                            dark:bg-[#262626]
                                                            hover:bg-slate-100
                                                            dark:hover:bg-[#333]
                                                            border
                                                            border-slate-100
                                                            dark:border-[#2a2a2a]
                                                            text-xs
                                                            font-bold
                                                            text-slate-600
                                                            dark:text-slate-300
                                                            rounded-lg
                                                            text-center
                                                            transition
                                                            flex
                                                            items-center
                                                            justify-center
                                                            gap-1
                                                        "
                                                    >
                                                        <Phone size={14} />
                                                        Gọi điện
                                                    </a>

                                                    <button
                                                        onClick={() => {
                                                            console.log("Nhắn tin");
                                                            // showFeatureToast?.("Nhắn tin");
                                                        }}
                                                        className="
                                                            flex-1
                                                            py-2
                                                            bg-slate-50
                                                            dark:bg-[#262626]
                                                            hover:bg-slate-100
                                                            dark:hover:bg-[#333]
                                                            border
                                                            border-slate-100
                                                            dark:border-[#2a2a2a]
                                                            text-xs
                                                            font-bold
                                                            text-slate-600
                                                            dark:text-slate-300
                                                            rounded-lg
                                                            transition
                                                            flex
                                                            items-center
                                                            justify-center
                                                            gap-1
                                                        "
                                                    >
                                                        <MessageSquare size={14} />
                                                        Nhắn tin
                                                    </button>

                                                </div>
                                            </div>
                                        </div>

                                        <AppointmentScheduleBox
                                            appointmentId={id}
                                            appointment={appointment}
                                            canEdit={canEdit}
                                            onUpdated={(patch) => setAppointment((prev) => prev ? ({ ...prev, ...patch } as any) : prev)}
                                        />

                                        <CustomerImageGallery channelCustomerId={appointment?.channel_customer_id} />
                                    </div>
                                </div>

                                <Modal
                                    title="Chỉnh sửa địa chỉ"
                                    open={addressModalOpen}
                                    onCancel={() => setAddressModalOpen(false)}
                                    footer={null}
                                    destroyOnClose
                                >
                                    <div className="flex flex-col gap-4 mt-2">
                                        <div>
                                            <label className="block mb-1">Tỉnh thành</label>
                                            <Select
                                                className="w-full"
                                                placeholder="Chọn tỉnh"
                                                value={addressModalForm.city || undefined}
                                                loading={loadingProvinces}
                                                showSearch
                                                filterOption={(input, option) => {
                                                    const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                                    return normalize(String(option?.label || '')).includes(normalize(input));
                                                }}
                                                onChange={(v) => {
                                                    setAddressModalForm({ ...addressModalForm, city: v || '', ward: '' });
                                                    if (v) loadAddressModalWards(v);
                                                    else setAddressModalWardOptions([]);
                                                }}
                                                options={provinceOptions}
                                                allowClear
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1">Phường xã</label>
                                            <Select
                                                className="w-full"
                                                placeholder="Chọn phường"
                                                value={addressModalForm.ward || undefined}
                                                disabled={!addressModalForm.city}
                                                loading={addressModalLoadingWards}
                                                showSearch
                                                filterOption={(input, option) => {
                                                    const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                                    return normalize(String(option?.label || '')).includes(normalize(input));
                                                }}
                                                onChange={(v) => setAddressModalForm({ ...addressModalForm, ward: v || '' })}
                                                options={addressModalWardOptions}
                                                allowClear
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1">Địa chỉ</label>
                                            <Input.TextArea
                                                rows={3}
                                                value={addressModalForm.address}
                                                onChange={(e) => setAddressModalForm({ ...addressModalForm, address: e.target.value })}
                                                placeholder="Nhập địa chỉ"
                                            />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button onClick={() => setAddressModalOpen(false)}>Hủy</Button>
                                            <Button type="primary" loading={savingAddress} onClick={saveAddress}>Lưu</Button>
                                        </div>
                                    </div>
                                </Modal>
                            </div>
                        )}
                        {activeTab === "history" && (
                            <div className="tab-box-full"><HistoryList history={history} statusColor={statusColor} /></div>
                        )}
                    </div>
                </Spin>
            </Layout>
        </div>
    );
}

function Field({ label, value, editing, onChange, icon }: any) {
    return (
        <div>
            <label>{label}</label>
            {editing ? (
                <Input value={value} onChange={(e) => onChange(e.target.value)} />
            ) : (
                <div className="flex items-center gap-2 mt-1">
                    {icon}
                    {value}
                </div>
            )}
        </div>
    );
}

function Note({ name, text }: any) {
    return (
        <div className="flex gap-2">
            <Avatar>{name[0]}</Avatar>
            <div>
                <div className="text-sm font-medium">{name}</div>
                <div className="text-xs text-gray-500">{text}</div>
            </div>
        </div>
    );
}